/**
 * Custom Hook: useReviewSession
 *
 * Manages card review session flow with SRS algorithm
 * Handles card navigation, rating submission, and statistics
 */

import { useState, useCallback, useRef, useEffect } from 'react'

export type Card = {
  id: string
  front: string
  back: string
  interval: number
  easeFactor: number
}

export type Rating = 'again' | 'hard' | 'good' | 'easy'

export type SessionState = 'idle' | 'active' | 'paused' | 'completed'

export interface SessionProgress {
  reviewed: number
  total: number
}

export interface SessionStatistics {
  again: number
  hard: number
  good: number
  easy: number
  duration: number // in seconds
}

export interface UseReviewSessionOptions {
  cards?: Card[]
  shuffle?: boolean
  autoStart?: boolean
  onSessionComplete?: (results: { statistics: SessionStatistics; reviewedCards: Card[] }) => void
  onCardRated?: (card: Card, rating: Rating, nextInterval: number) => void
}

export interface UseReviewSessionReturn {
  // State
  sessionState: SessionState
  currentCard: Card | null
  progress: SessionProgress
  statistics: SessionStatistics

  // Actions
  startSession: (cards: Card[]) => void
  submitRating: (rating: Rating) => Promise<void>
  skipCard: () => Promise<void>
  pauseSession: () => void
  resumeSession: () => void
  resetSession: () => void
}

/**
 * Card review session management hook
 *
 * Features:
 * - Session state management (idle/active/paused/completed)
 * - SRS rating system (Again/Hard/Good/Easy)
 * - Progress tracking
 * - Card navigation
 * - Statistics calculation
 * - Optional callbacks for events
 */
export function useReviewSession(options: UseReviewSessionOptions = {}): UseReviewSessionReturn {
  const { cards: initialCards, shuffle = false, onSessionComplete, onCardRated } = options

  // Session state
  const [sessionState, setSessionState] = useState<SessionState>('idle')
  const [currentCard, setCurrentCard] = useState<Card | null>(null)
  const [progress, setProgress] = useState<SessionProgress>({ reviewed: 0, total: 0 })
  const [statistics, setStatistics] = useState<SessionStatistics>({
    again: 0,
    hard: 0,
    good: 0,
    easy: 0,
    duration: 0,
  })

  // Internal state
  const cardQueueRef = useRef<Card[]>([])
  const startTimeRef = useRef<number>(0)
  const reviewedCardsRef = useRef<Card[]>([])
  const hasInitializedRef = useRef(false)

  // Update duration timer
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null

    if (sessionState === 'active') {
      interval = setInterval(() => {
        setStatistics((prev) => ({
          ...prev,
          duration: Math.floor((Date.now() - startTimeRef.current) / 1000),
        }))
      }, 1000)
    }

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [sessionState])

  // Shuffle array utility
  const shuffleCards = useCallback((cards: Card[]): Card[] => {
    const shuffled = [...cards]
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled
  }, [])

  // Start session
  const startSession = useCallback((cards: Card[]) => {
    if (cards.length === 0) return

    const queue = shuffle ? shuffleCards([...cards]) : [...cards]
    cardQueueRef.current = queue
    setCurrentCard(queue[0])
    setProgress({ reviewed: 0, total: cards.length })
    setStatistics({ again: 0, hard: 0, good: 0, easy: 0, duration: 0 })
    reviewedCardsRef.current = []
    startTimeRef.current = Date.now()
    setSessionState('active')
  }, [shuffle, shuffleCards])

  // Move to next card
  const moveToNextCard = useCallback(() => {
    const remainingCards = cardQueueRef.current.length - 1

    cardQueueRef.current.shift() // Remove current card

    if (remainingCards === 0) {
      // Session complete
      setCurrentCard(null)
      setProgress((prev) => ({ ...prev, reviewed: prev.total }))
      setSessionState('completed')

      if (onSessionComplete) {
        onSessionComplete({
          statistics,
          reviewedCards: reviewedCardsRef.current,
        })
      }
    } else {
      // Move to next card
      setCurrentCard(cardQueueRef.current[0])
      setProgress((prev) => ({ ...prev, reviewed: prev.reviewed + 1 }))
    }
  }, [statistics, onSessionComplete])

  // Submit rating
  const submitRating = useCallback(
    async (rating: Rating) => {
      if (sessionState !== 'active' || !currentCard) return

      // Update statistics
      setStatistics((prev) => ({
        ...prev,
        [rating]: prev[rating] + 1,
      }))

      // Track reviewed card
      reviewedCardsRef.current.push(currentCard)

      // Calculate next interval (simplified SM-2 algorithm)
      const nextInterval = calculateNextInterval(currentCard, rating)

      // Callback
      if (onCardRated) {
        onCardRated(currentCard, rating, nextInterval)
      }

      // Move to next card
      moveToNextCard()

      // Small delay to allow state updates to flush
      await new Promise(resolve => setTimeout(resolve, 0))
    },
    [sessionState, currentCard, moveToNextCard, onCardRated]
  )

  // Skip current card
  const skipCard = useCallback(async () => {
    if (sessionState !== 'active') return

    moveToNextCard()

    // Small delay to allow state updates to flush
    await new Promise(resolve => setTimeout(resolve, 0))
  }, [sessionState, moveToNextCard])

  // Pause session
  const pauseSession = useCallback(() => {
    if (sessionState === 'active') {
      setSessionState('paused')
    }
  }, [sessionState])

  // Resume session
  const resumeSession = useCallback(() => {
    if (sessionState === 'paused') {
      setSessionState('active')
    }
  }, [sessionState])

  // Reset session
  const resetSession = useCallback(() => {
    setSessionState('idle')
    setCurrentCard(null)
    setProgress({ reviewed: 0, total: 0 })
    setStatistics({ again: 0, hard: 0, good: 0, easy: 0, duration: 0 })
    cardQueueRef.current = []
    reviewedCardsRef.current = []
    hasInitializedRef.current = false
  }, [])

  // Initialize with cards if autoStart enabled
  useEffect(() => {
    if (initialCards && initialCards.length > 0 && sessionState === 'idle' && !hasInitializedRef.current) {
      hasInitializedRef.current = true
      startSession(initialCards)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run on mount

  return {
    sessionState,
    currentCard,
    progress,
    statistics,
    startSession,
    submitRating,
    skipCard,
    pauseSession,
    resumeSession,
    resetSession,
  }
}

/**
 * Simplified SM-2 algorithm for calculating next review interval
 */
function calculateNextInterval(card: Card, rating: Rating): number {
  const { interval, easeFactor } = card

  let newInterval = interval

  switch (rating) {
    case 'again':
      newInterval = 1 // Reset to 1 day
      break
    case 'hard':
      newInterval = Math.max(1, Math.floor(interval * 1.2))
      break
    case 'good':
      newInterval = Math.floor(interval * easeFactor)
      break
    case 'easy':
      newInterval = Math.floor(interval * easeFactor * 1.3)
      break
  }

  return Math.max(1, newInterval)
}
